import * as THREE from 'three';
import { loadGlb } from '../assets/loadGlb';
import { lookDotTo, reachDistance, type InteractTarget, type Xyz } from '../interact/Interact';
import type { Aabb2, DinoKind, DinoKit } from './kits';

export type DinoState = 'wander' | 'flee' | 'alert' | 'combat';

export type DinoSnap = {
  id: string;
  x: number;
  y: number;
  z: number;
  hp: number;
  kind: DinoKind;
  taxon: string;
};

export type PackRole = 'rush' | 'circle';

export type FaunaSense = {
  hp: number;
  held: string | null;
  sneak: boolean;
  speed: number;
  sprint?: boolean;
  swimming?: boolean;
  night?: boolean;
  area?: string;
  camera: THREE.Camera;
  interact: boolean;
  hurt(amount: number): void;
  blind?(seconds: number): void;
};

const FRILL_FOLD = 0.22;
const FRILL_FAN = 1.05;
const TELL_S = 0.6;
const SPIT_DMG = 8;
const SPIT_BLIND_S = 2.4;
const SPIT_CD = 2.6;
const PECK_DMG = 3;
const PECK_CD = 1.4;
const RAPTOR_SLASH = 14;
const RAPTOR_CD = 1.0;
const RAPTOR_SPRINT_M = 18;
const RAPTOR_ORBIT = 6.5;
const DEINO_SLASH = 20;
const DEINO_CD = 1.35;
const DEINO_HUNT_M = 12;
const PARA_STAMP = 10;
const PARA_STAMP_CD = 1.2;
const PARA_STAMP_M = 5.5;
const TRIKE_CHARGE = 24;
const TRIKE_CD = 1.5;
const TRIKE_FRONT = 0.5;
const STEGO_TAIL = 18;
const STEGO_CD = 1.4;
const ANKY_CLUB = 20;
const ANKY_CD = 1.8;
const CARNO_BITE = 16;
const CARNO_CD = 1.1;
const SPINO_BITE = 36;
const SPINO_WATER = 48;
const SPINO_CD = 1.3;
const SPINO_WATER_M = 10;
/** Rex only closes if Rowan is loud and inside this. Quiet walk past is the run. */
const REX_RUN_M = 14;
const REX_DROP_M = 18;
const REX_BITE = 28;
const REX_CD = 3.2;
const BARY_LUNGE = 18;
const BARY_CD = 1.5;
const PTERA_PECK = 6;
const PTERA_CD = 1.6;
const PTERA_CRUISE = 6.6;
/** Dive into spear height so Rowan can answer a peck. */
const PTERA_DIVE = 1.95;
const PTERA_DIVE_M = 7;
const FORGET_S = 1.2;
const SPEAR_DMG = 22;
const SPEAR_RANGE = 2.8;
const LOOK_MIN = 0.55;
const NOISY_SPEED = 1.35;

const _look = new THREE.Vector3();

export class DinoActor {
  readonly id: string;
  readonly kit: DinoKit;
  readonly mesh: THREE.Group;
  readonly bounds: Aabb2;
  hp: number;
  state: DinoState = 'wander';
  x: number;
  y = 0;
  z: number;
  yaw = 0;
  /** XZ wander sits on this plane. Cave / vale hunters are not at y=0. */
  readonly floorY: number;
  packRole: PackRole = 'rush';
  packSign = 1;
  /** CARRY: wander slower. 1 = kit speed. */
  wanderScale = 1;
  /** BURN: fauna bolt instead of hunting. */
  forcedFlee = false;
  private wx: number;
  private wz: number;
  private orbit = 0;
  private retarget = 0;
  private tell = 0;
  private tellCue = false;
  private spitCue = false;
  private cooldown = 0;
  private forget = 0;
  private gait = 0;
  private readonly frillL: THREE.Object3D | null;
  private readonly frillR: THREE.Object3D | null;
  private readonly legL: THREE.Object3D | null;
  private readonly legR: THREE.Object3D | null;
  private mixer: THREE.AnimationMixer | null = null;
  private idleAct: THREE.AnimationAction | null = null;
  private walkAct: THREE.AnimationAction | null = null;
  private runAct: THREE.AnimationAction | null = null;
  private currentAct: THREE.AnimationAction | null = null;

  constructor(id: string, kit: DinoKit, x: number, z: number, bounds: Aabb2, floorY = 0) {
    this.id = id;
    this.kit = kit;
    this.hp = kit.hp;
    this.x = x;
    this.z = z;
    this.y = floorY;
    this.floorY = floorY;
    this.wx = x;
    this.wz = z;
    this.bounds = bounds;
    this.mesh = kit.build();
    this.mesh.name = id;
    this.mesh.userData.id = id;
    this.mesh.userData.kind = kit.kind;
    this.frillL = this.mesh.getObjectByName('frillL') ?? null;
    this.frillR = this.mesh.getObjectByName('frillR') ?? null;
    this.legL = this.mesh.getObjectByName('legL') ?? null;
    this.legR = this.mesh.getObjectByName('legR') ?? null;
    this.syncMesh(0);
    if (kit.glb) {
      void this.tryLoadGlb(kit.glb);
    } else if (kit.kind === 'raptor') {
      void this.tryLoadGlb('/models/dino/dino_raptor.glb');
    }
  }

  get kind(): DinoKind {
    return this.kit.kind;
  }

  get live(): boolean {
    return this.hp > 0;
  }

  /** Combat rush from a camp-breaker (trike / rex / carno / stego). */
  get charging(): boolean {
    if (!this.live || this.state !== 'combat') {
      return false;
    }
    const kind = this.kit.kind;
    return kind === 'trike' || kind === 'rex' || kind === 'carno' || kind === 'stego';
  }

  get position(): Xyz {
    const y = this.kit.kind === 'ptera' ? this.y : this.floorY + this.kit.hip;
    return { x: this.x, y, z: this.z };
  }

  interactTarget(): InteractTarget {
    return {
      id: this.id,
      prompt: this.live ? 'Click — Spear' : '',
      position: this.position,
      radius:
        this.kit.kind === 'rex' || this.kit.kind === 'spino'
          ? 1.8
          : this.kit.kind === 'trike' || this.kit.kind === 'stego' || this.kit.kind === 'anky' || this.kit.kind === 'para'
            ? 1.4
            : this.kit.kind === 'baryonyx' || this.kit.kind === 'carno'
              ? 1.2
              : this.kit.kind === 'diloph' || this.kit.kind === 'galli'
                ? 0.7
                : this.kit.kind === 'raptor' || this.kit.kind === 'troodon' || this.kit.kind === 'deinonychus'
                  ? 0.5
                  : this.kit.kind === 'ptera'
                    ? 1.6
                    : 0.28,
      live: this.live,
    };
  }

  snap(): DinoSnap {
    return {
      id: this.id,
      x: this.x,
      y: this.y,
      z: this.z,
      hp: this.hp,
      kind: this.kit.kind,
      taxon: this.kit.taxon,
    };
  }

  takeTell(): boolean {
    const hit = this.tellCue;
    this.tellCue = false;
    return hit;
  }

  takeSpit(): boolean {
    const hit = this.spitCue;
    this.spitCue = false;
    return hit;
  }

  beginCharge(x: number, z: number): void {
    if (!this.live) {
      return;
    }
    this.state = 'combat';
    this.wx = x;
    this.wz = z;
  }

  takeHit(amount: number): void {
    if (!this.live || amount <= 0) {
      return;
    }
    this.hp = Math.max(0, this.hp - amount);
    if (!this.live) {
      this.state = 'wander';
      this.mesh.rotation.z = Math.PI * 0.5;
      this.mesh.position.y = this.floorY + 0.08;
    }
  }

  /** CARRY flask sickness — 0.01 hp / s. */
  sicken(dt: number, rate = 0.01): void {
    if (!this.live || dt <= 0 || rate <= 0) {
      return;
    }
    this.takeHit(rate * dt);
  }

  inSpearCone(camera: THREE.Camera, player: Xyz): boolean {
    if (!this.live) {
      return false;
    }
    const target = this.interactTarget();
    if (reachDistance(player, target) > SPEAR_RANGE) {
      return false;
    }
    camera.getWorldDirection(_look);
    const high = target.position.y - player.y > 1.1;
    const origin = high ? { x: player.x, y: target.position.y, z: player.z } : player;
    const look = high ? _look.clone().setY(0) : _look;
    if (high && look.lengthSq() > 1e-8) {
      look.normalize();
    }
    return lookDotTo(origin, look, target.position) >= LOOK_MIN;
  }

  tick(dt: number, player: Xyz, sense?: FaunaSense): void {
    if (!this.live) {
      return;
    }
    this.cooldown = Math.max(0, this.cooldown - dt);
    this.retarget = Math.max(0, this.retarget - dt);
    const dist = Math.hypot(player.x - this.x, player.z - this.z);
    this.think(dt, dist, player, sense);
    const speed = this.gaitSpeed();
    this.drive(dt, speed);
    this.holdPteraHeight();
    this.animate(dt, speed);
    this.syncMesh(dt);
  }

  private think(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    if (this.forcedFlee) {
      this.state = 'flee';
      this.aimAway(player, 8);
      return;
    }
    if (this.kit.kind === 'compy') {
      this.thinkCompy(dist, player, sense);
      return;
    }
    if (this.kit.kind === 'raptor' || this.kit.kind === 'troodon') {
      this.thinkRaptor(dt, dist, player, sense);
      return;
    }
    if (this.kit.kind === 'deinonychus') {
      this.thinkDeinonychus(dt, dist, player, sense);
      return;
    }
    if (this.kit.kind === 'rex') {
      this.thinkRex(dt, dist, player, sense);
      return;
    }
    if (this.kit.kind === 'para') {
      this.thinkPara(dist, player, sense);
      return;
    }
    if (this.kit.kind === 'galli') {
      this.thinkGalli(dist, player, sense);
      return;
    }
    if (this.kit.kind === 'trike') {
      this.thinkTrike(dist, player, sense);
      return;
    }
    if (this.kit.kind === 'stego') {
      this.thinkStego(dist, player, sense);
      return;
    }
    if (this.kit.kind === 'anky') {
      this.thinkAnky(dist, player, sense);
      return;
    }
    if (this.kit.kind === 'carno') {
      this.thinkCarno(dt, dist, player, sense);
      return;
    }
    if (this.kit.kind === 'spino') {
      this.thinkSpino(dist, player, sense);
      return;
    }
    if (this.kit.kind === 'ptera') {
      this.thinkPtera(dt, dist, player, sense);
      return;
    }
    if (this.kit.kind === 'baryonyx') {
      this.thinkBaryonyx(dt, dist, player, sense);
      return;
    }
    this.thinkDiloph(dt, dist, player, sense);
  }

  private thinkCompy(dist: number, player: Xyz, sense?: FaunaSense): void {
    const dying = (sense?.hp ?? 100) < this.kit.lethalHp;
    if (dying && dist < this.kit.fleeRange) {
      this.state = 'combat';
      this.wx = player.x;
      this.wz = player.z;
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(PECK_DMG);
        this.cooldown = PECK_CD;
      }
      return;
    }
    if (dist < this.kit.fleeRange) {
      this.state = 'flee';
      this.aimAway(player, 6);
      // steal if meat later — no meat item on this slice
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkDiloph(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    const noisy = !!sense && (sense.speed >= NOISY_SPEED || (!sense.sneak && sense.speed > 0.35));
    if (dist <= this.kit.spitRange) {
      this.state = 'combat';
      this.forget = 0;
      this.face(player);
      if (this.kit.kind === 'diloph' && this.tell <= 0) {
        this.tellCue = true;
      }
      this.tell += dt;
      if (this.tell >= TELL_S && this.cooldown <= 0) {
        sense?.hurt(SPIT_DMG);
        sense?.blind?.(SPIT_BLIND_S);
        this.cooldown = SPIT_CD;
        this.tell = 0;
        if (this.kit.kind === 'diloph') {
          this.spitCue = true;
        }
      }
      return;
    }
    this.tell = 0;
    if (dist <= this.kit.hearRange && noisy) {
      this.state = 'alert';
      this.forget = 0;
      this.face(player);
      return;
    }
    if (this.state === 'alert' || this.state === 'combat') {
      this.forget += dt;
      if (this.forget < FORGET_S) {
        this.state = 'alert';
        this.face(player);
        return;
      }
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkRaptor(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    const nightHunt = !!sense?.night && sense.area === 'deep_jungle';
    const sprintHunt = !!sense?.sprint && dist <= RAPTOR_SPRINT_M;
    if (nightHunt || sprintHunt) {
      this.state = 'combat';
      this.forget = 0;
      if (this.packRole === 'circle') {
        this.orbit += dt * 2.2 * this.packSign;
        this.wx = player.x + Math.cos(this.orbit) * RAPTOR_ORBIT;
        this.wz = player.z + Math.sin(this.orbit) * RAPTOR_ORBIT;
      } else {
        this.wx = player.x;
        this.wz = player.z;
        if (dist <= this.kit.peckRange && this.cooldown <= 0) {
          sense?.hurt(RAPTOR_SLASH);
          this.cooldown = RAPTOR_CD;
        }
      }
      return;
    }
    if (this.state === 'combat') {
      this.forget += dt;
      if (this.forget < FORGET_S) {
        this.face(player);
        return;
      }
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkRex(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    const loud = isNoisy(sense);
    if (dist > REX_DROP_M) {
      this.state = 'wander';
      this.maybeWander();
      return;
    }
    if (loud && dist <= REX_RUN_M) {
      this.state = 'combat';
      this.forget = 0;
      this.wx = player.x;
      this.wz = player.z;
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(REX_BITE);
        this.cooldown = REX_CD;
      }
      return;
    }
    if (this.state === 'combat') {
      this.forget += dt;
      if (this.forget < FORGET_S) {
        this.face(player);
        return;
      }
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkBaryonyx(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    const wet = !!sense?.swimming || (sense?.area === 'riverlands' && player.y < 1.55);
    const lunge = dist <= this.kit.peckRange && (wet || isNoisy(sense));
    if (lunge) {
      this.state = 'combat';
      this.forget = 0;
      this.wx = player.x;
      this.wz = player.z;
      if (this.cooldown <= 0) {
        sense?.hurt(BARY_LUNGE);
        this.cooldown = BARY_CD;
      }
      return;
    }
    if (wet && dist <= this.kit.hearRange) {
      this.state = 'alert';
      this.face(player);
      return;
    }
    if (this.state === 'combat' || this.state === 'alert') {
      this.forget += dt;
      if (this.forget < FORGET_S) {
        this.face(player);
        return;
      }
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkDeinonychus(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    const nightHunt = !!sense?.night && sense.area === 'deep_jungle';
    const close = dist <= DEINO_HUNT_M;
    const loud = isNoisy(sense) && dist <= this.kit.hearRange;
    if (nightHunt || close || loud) {
      this.state = 'combat';
      this.forget = 0;
      this.wx = player.x;
      this.wz = player.z;
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(DEINO_SLASH);
        this.cooldown = DEINO_CD;
      }
      return;
    }
    if (this.state === 'combat') {
      this.forget += dt;
      if (this.forget < FORGET_S) {
        this.face(player);
        return;
      }
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkPara(dist: number, player: Xyz, sense?: FaunaSense): void {
    const noisy = isNoisy(sense);
    if (dist <= PARA_STAMP_M && noisy) {
      this.state = 'combat';
      this.aimAway(player, 14);
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(PARA_STAMP);
        this.cooldown = PARA_STAMP_CD;
      }
      return;
    }
    if (dist < this.kit.fleeRange) {
      this.state = 'flee';
      this.aimAway(player, 10);
      return;
    }
    if (dist <= this.kit.hearRange) {
      this.state = 'alert';
      this.face(player);
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkGalli(dist: number, player: Xyz, sense?: FaunaSense): void {
    if (dist < this.kit.fleeRange || (dist < this.kit.hearRange && isNoisy(sense))) {
      this.state = 'flee';
      this.aimAway(player, 16);
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkTrike(dist: number, player: Xyz, sense?: FaunaSense): void {
    const front = this.facingDot(player) >= TRIKE_FRONT;
    if (this.state === 'combat') {
      const arrived = Math.hypot(this.wx - this.x, this.wz - this.z) < 0.4;
      if (arrived || dist > this.kit.hearRange + 6) {
        this.state = 'wander';
        this.maybeWander();
        return;
      }
      if (dist <= this.kit.peckRange && this.cooldown <= 0 && this.facingDot(player) > 0.2) {
        sense?.hurt(TRIKE_CHARGE);
        this.cooldown = TRIKE_CD;
      }
      return;
    }
    if (dist <= this.kit.hearRange && front) {
      this.state = 'combat';
      this.yaw = Math.atan2(player.x - this.x, player.z - this.z);
      const run = 16;
      this.wx = this.x + Math.sin(this.yaw) * run;
      this.wz = this.z + Math.cos(this.yaw) * run;
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkStego(dist: number, player: Xyz, sense?: FaunaSense): void {
    const sideOrRear = this.facingDot(player) < 0.2;
    if (dist <= this.kit.peckRange + 0.6 && sideOrRear) {
      this.state = 'combat';
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(STEGO_TAIL);
        this.cooldown = STEGO_CD;
      }
      return;
    }
    if (dist < 4) {
      this.state = 'alert';
      this.face(player);
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkAnky(dist: number, player: Xyz, sense?: FaunaSense): void {
    if (dist <= this.kit.peckRange + 0.5) {
      this.state = 'combat';
      this.wx = player.x;
      this.wz = player.z;
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(ANKY_CLUB);
        this.cooldown = ANKY_CD;
      }
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkCarno(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    if (dist <= this.kit.hearRange) {
      this.state = 'combat';
      this.forget = 0;
      this.wx = player.x;
      this.wz = player.z;
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(CARNO_BITE);
        this.cooldown = CARNO_CD;
      }
      return;
    }
    if (this.state === 'combat') {
      this.forget += dt;
      if (this.forget < FORGET_S) {
        this.face(player);
        return;
      }
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkSpino(dist: number, player: Xyz, sense?: FaunaSense): void {
    const inWater = !!sense?.swimming || (sense?.area === 'riverlands' && player.y < 1.55);
    const close = dist <= this.kit.peckRange;
    const waterThreat = inWater && dist <= SPINO_WATER_M;
    if (close || waterThreat) {
      this.state = 'combat';
      this.wx = player.x;
      this.wz = player.z;
      if (this.cooldown <= 0) {
        sense?.hurt(inWater ? SPINO_WATER : SPINO_BITE);
        this.cooldown = SPINO_CD;
      }
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private thinkPtera(dt: number, dist: number, player: Xyz, sense?: FaunaSense): void {
    const sneak = !!sense?.sneak && !isNoisy(sense);
    if (dist <= PTERA_DIVE_M && !sneak) {
      this.state = 'combat';
      this.wx = player.x;
      this.wz = player.z;
      if (dist <= this.kit.peckRange && this.cooldown <= 0) {
        sense?.hurt(PTERA_PECK);
        this.cooldown = PTERA_CD;
      }
      return;
    }
    if (dist <= this.kit.hearRange) {
      this.state = 'alert';
      this.orbit += dt * 0.8;
      this.wx = player.x + Math.cos(this.orbit) * 10;
      this.wz = player.z + Math.sin(this.orbit) * 10;
      return;
    }
    this.state = 'wander';
    this.maybeWander();
  }

  private facingDot(player: Xyz): number {
    const dx = player.x - this.x;
    const dz = player.z - this.z;
    const mag = Math.hypot(dx, dz) || 1;
    return (Math.sin(this.yaw) * dx + Math.cos(this.yaw) * dz) / mag;
  }

  private holdPteraHeight(): void {
    if (this.kit.kind !== 'ptera' || !this.live) {
      return;
    }
    this.y = this.floorY + (this.state === 'combat' ? PTERA_DIVE : PTERA_CRUISE);
  }

  private gaitSpeed(): number {
    if (this.state === 'flee') {
      return this.kit.fleeSpeed;
    }
    if (this.state === 'combat') {
      return this.kit.combatSpeed;
    }
    if (this.state === 'alert') {
      return this.kit.kind === 'ptera' ? this.kit.wanderSpeed : 0;
    }
    return this.kit.wanderSpeed * this.wanderScale;
  }

  private drive(dt: number, speed: number): void {
    if (speed <= 0) {
      return;
    }
    const dx = this.wx - this.x;
    const dz = this.wz - this.z;
    const mag = Math.hypot(dx, dz);
    if (mag < 0.25) {
      if (this.state === 'wander') {
        this.pickWander();
      }
      return;
    }
    const step = Math.min(speed * dt, mag);
    const nx = this.x + (dx / mag) * step;
    const nz = this.z + (dz / mag) * step;
    this.x = clamp(nx, this.bounds.minX, this.bounds.maxX);
    this.z = clamp(nz, this.bounds.minZ, this.bounds.maxZ);
    this.y = this.floorY;
    this.yaw = Math.atan2(dx, dz);
  }

  private maybeWander(): void {
    if (this.retarget <= 0) {
      this.pickWander();
    }
  }

  private pickWander(): void {
    const { minX, maxX, minZ, maxZ } = this.bounds;
    this.wx = minX + Math.random() * (maxX - minX);
    this.wz = minZ + Math.random() * (maxZ - minZ);
    this.retarget = 2.2 + Math.random() * 2.4;
  }

  private aimAway(player: Xyz, run: number): void {
    const dx = this.x - player.x;
    const dz = this.z - player.z;
    const mag = Math.hypot(dx, dz) || 1;
    this.wx = clamp(this.x + (dx / mag) * run, this.bounds.minX, this.bounds.maxX);
    this.wz = clamp(this.z + (dz / mag) * run, this.bounds.minZ, this.bounds.maxZ);
  }

  private face(player: Xyz): void {
    this.wx = this.x;
    this.wz = this.z;
    this.yaw = Math.atan2(player.x - this.x, player.z - this.z);
  }

  private animate(dt: number, speed: number): void {
    if (this.mixer) {
      this.blendClips(speed);
      this.mixer.update(dt);
      return;
    }
    if (speed > 0.15) {
      this.gait += dt * (6 + speed * 1.4);
    }
    const swing = Math.sin(this.gait) * Math.min(0.45, speed * 0.12);
    if (this.legL) {
      this.legL.rotation.x = swing;
    }
    if (this.legR) {
      this.legR.rotation.x = -swing;
    }
    const open = this.state === 'alert' || this.state === 'combat' ? FRILL_FAN : FRILL_FOLD;
    if (this.frillL) {
      this.frillL.rotation.y = THREE.MathUtils.damp(this.frillL.rotation.y, open, 8, dt);
    }
    if (this.frillR) {
      this.frillR.rotation.y = THREE.MathUtils.damp(this.frillR.rotation.y, -open, 8, dt);
    }
  }

  private syncMesh(dt: number): void {
    const bob = this.state === 'wander' || this.state === 'flee' || this.state === 'combat'
      ? Math.sin(this.gait * 2) * 0.012
      : 0;
    this.mesh.position.set(this.x, this.y + bob, this.z);
    this.mesh.rotation.y = this.yaw;
    void dt;
  }

  /** Swap primitive raptor boxes for the authored GLB. Keep boxes if the file is missing. */
  private async tryLoadGlb(url: string): Promise<void> {
    let hero: THREE.Group;
    try {
      hero = await loadGlb(url);
    } catch {
      return;
    }
    hero.name = url.split('/').pop()?.replace('.glb', '') ?? 'dino';
    hero.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
    alignDinoGlb(hero);
    for (const child of this.mesh.children) {
      child.visible = false;
    }
    this.mesh.add(hero);
    const raw = hero.userData.animations;
    const clips = Array.isArray(raw) ? (raw as THREE.AnimationClip[]) : [];
    if (clips.length > 0) {
      this.mixer = new THREE.AnimationMixer(hero);
      const idle = THREE.AnimationClip.findByName(clips, 'dino_idle') ?? clips[0];
      const walk =
        THREE.AnimationClip.findByName(clips, 'dino_walk') ??
        THREE.AnimationClip.findByName(clips, 'dino_run') ??
        idle;
      const run =
        THREE.AnimationClip.findByName(clips, 'dino_run') ??
        walk;
      if (idle) {
        this.idleAct = this.mixer.clipAction(idle);
        this.walkAct = walk ? this.mixer.clipAction(walk) : this.idleAct;
        this.runAct = run ? this.mixer.clipAction(run) : this.walkAct;
        for (const act of [this.idleAct, this.walkAct, this.runAct]) {
          act.enabled = true;
          act.setLoop(THREE.LoopRepeat, Infinity);
          act.setEffectiveWeight(0);
        }
        this.idleAct.setEffectiveWeight(1);
        this.idleAct.play();
        this.currentAct = this.idleAct;
      }
    }
  }

  private blendClips(speed: number): void {
    if (!this.idleAct || !this.walkAct || !this.runAct) {
      return;
    }
    const want =
      speed < 0.2 ? this.idleAct : speed >= this.kit.fleeSpeed * 0.72 ? this.runAct : this.walkAct;
    if (want !== this.currentAct) {
      want.reset();
      want.setEffectiveWeight(1);
      want.fadeIn(0.14);
      want.play();
      this.currentAct?.fadeOut(0.14);
      this.currentAct = want;
    }
    if (this.currentAct === this.runAct) {
      this.runAct.setEffectiveTimeScale(speed > 8 ? 1.25 : 1);
    } else if (this.currentAct === this.walkAct) {
      this.walkAct.setEffectiveTimeScale(speed > 3.2 ? 1.15 : 1);
    }
  }
}

export function spearHit(dino: DinoActor): void {
  dino.takeHit(SPEAR_DMG);
}

/**
 * Authored dino GLBs face −Z (head world z < 0). Primitive kits and
 * locomotion yaw treat +Z as the nose. Spin the hero so walk matches heading.
 */
export function alignDinoGlb(hero: THREE.Object3D): void {
  hero.updateMatrixWorld(true);
  const head = hero.getObjectByName('head');
  let noseZ = 0;
  if (head) {
    const p = new THREE.Vector3();
    head.getWorldPosition(p);
    hero.worldToLocal(p);
    noseZ = p.z;
  }
  if (noseZ <= 0) {
    hero.rotation.y += Math.PI;
  }
}

function isNoisy(sense?: FaunaSense): boolean {
  return !!sense && (sense.speed >= NOISY_SPEED || (!sense.sneak && sense.speed > 0.35) || !!sense.sprint);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
